import { useState } from "react"
import Button from "aldoo-ra/Button"
import Tooltip from "aldoo-ra/Tooltip"
import { CheckIcon } from "@heroicons/react/24/outline"
import { ChevronDownIcon } from "@heroicons/react/24/outline"
import { store } from "aldoo-ra/Store"
import PubSub from "pubsub-js"
import { useStuck, getPlanPriceLabel, getValue, ValueCell, resolveHighlight } from "./helpers"

export default function MobileFeatureTable({ plans, features, categories, texts, storeData, activePid, billing }) {
  const [leftIdx, setLeftIdx] = useState(0)
  const [rightIdx, setRightIdx] = useState(1)
  const [leftOpen, setLeftOpen] = useState(false)
  const [rightOpen, setRightOpen] = useState(false)
  const [sentinelRef, stuck] = useStuck(64)

  const leftPlan = plans[leftIdx]
  const rightPlan = plans[rightIdx]
  const leftPrice = stuck ? getPlanPriceLabel(leftPlan, storeData, billing, texts) : ""
  const rightPrice = stuck ? getPlanPriceLabel(rightPlan, storeData, billing, texts) : ""

  const leftOptions = plans.filter((_, i) => i !== rightIdx)
  const rightOptions = plans.filter((_, i) => i !== leftIdx)

  const handleBuyPlan = (plan) => {
    const productId = typeof plan.product === "string" ? plan.product : (plan.product?.item || plan.product?._id)
    const product = storeData.products?.[productId]
    const priceId = product?.price?._id || product?.price || product?.meta?.prices?.monthly
    const storePrice = storeData.prices?.[priceId]
    if (!storePrice?.price_model) return
    const getModelByInterval = (interval) =>
      storePrice.price_model.find((m) => m.phases?.[0]?.interval === interval) ?? null
    const monthlyModel = getModelByInterval("month")
    const annualModel = getModelByInterval("year")
    const selectedModel = billing === "annual" ? (annualModel || monthlyModel) : (monthlyModel || annualModel)
    if (!selectedModel) return
    const webEntry = selectedModel.real_product_id?.find((r) => {
      const platform = typeof r.platform === "object" ? r.platform?.value : r.platform
      return platform === "Web"
    })
    store.addToCart({
      product: {
        pid: product?.pid,
        product_id: storePrice._id,
        store_id: webEntry?.id || null,
        name: product?.name || plan.name,
        description: product?.description,
        currency: selectedModel.currency || "USD",
        price_model: [{ ...selectedModel, primary: true }],
        duration: billing === "annual" ? texts?.perYear : texts?.perMonth,
        thumbnail: product?.thumbnail,
      },
      proceedToCart: true,
    })
  }

  const renderPlanCTA = (plan) => {
    const productId = typeof plan.product === "string" ? plan.product : (plan.product?.item || plan.product?._id)
    const product = storeData.products?.[productId]
    const isCurrent = activePid && product?.pid === activePid
    const isFree = plan.id === "free"
    const isEnterprise = plan.id === "enterprise"

    if (isCurrent) {
      return (
        <Button variant="primary_sm" className="w-full text-xs sm:text-sm bg-success hover:bg-success opacity-80 cursor-default" disabled>
          {texts?.currentPlan}
        </Button>
      )
    }
    if (isEnterprise) {
      return (
        <Button variant="primary_sm" className="w-full text-xs sm:text-sm" onClick={() => PubSub.publish("open-contacts", { category: "sales" })}>
          {plan.cta_label || texts?.contactUs}
        </Button>
      )
    }
    if (isFree) {
      return (
        <Button variant="secondary_sm" className="w-full text-xs sm:text-sm" onClick={() => handleBuyPlan(plan)}>
          {plan.cta_label || texts?.getStarted}
        </Button>
      )
    }
    return (
      <Button variant="primary_sm" className="w-full text-xs sm:text-sm" onClick={() => handleBuyPlan(plan)}>
        {plan.cta_label || texts?.buy || "Buy"}
      </Button>
    )
  }

  const grouped = categories.map((cat) => {
    const catId = cat._id
    const catFeatures = features.filter((f) => {
      const fCatId = typeof f.category === "string" ? f.category : f.category?._id
      return fCatId === catId
    })
    return { category: cat, features: catFeatures }
  })

  const categorisedIds = new Set(categories.map((c) => c._id))
  const uncategorised = features.filter((f) => {
    const fCatId = typeof f.category === "string" ? f.category : f.category?._id
    return !fCatId || !categorisedIds.has(fCatId)
  })
  if (uncategorised.length > 0) {
    grouped.push({ category: { name: texts?.other }, features: uncategorised })
  }

  const renderValue = (feature, plan) => {
    const productId = typeof plan.product === "string" ? plan.product : (plan.product?.item || plan.product?._id)
    const product = storeData?.products?.[productId]
    const rawValue = getValue(feature, plan)
    const resolvedValue = resolveHighlight(rawValue, product)
    return <ValueCell value={resolvedValue} texts={texts} />
  }

  return (
    <div className="rounded-3xl">
      <div ref={sentinelRef} className="h-0" />
      <div className={`flex bg-neutral_1 border-b border-bordered p-2 sticky top-16 z-30 ${stuck ? "" : "rounded-t-xl"}`}>
        {/* Left selector */}
        <div className="flex-1 flex flex-col gap-2">
          <div className="relative">
            <button
              className="w-full flex items-center justify-between gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-text"
              onClick={() => { setLeftOpen((v) => !v); setRightOpen(false) }}
            >
              <span className="flex flex-col items-start min-w-0">
                <span className="text-base sm:text-lg text-white">
                  {leftPlan.name}
                </span>

                <span className={`overflow-hidden transition-all duration-300 ease-out ${stuck && leftPrice ? "max-h-5 opacity-100" : "max-h-0 opacity-0"}`}>
                  <span className="text-xs font-medium text-neutral_1">{leftPrice}</span>
                </span>
              </span>

              <ChevronDownIcon className={`w-5 h-5 shrink-0 text-white stroke-2 transition-transform duration-200 ${leftOpen ? "rotate-180" : ""}`} />
            </button>

            {leftOpen && (
              <div className="absolute z-20 mt-1 w-full rounded-xl border border-bordered bg-paper shadow-lg overflow-hidden">
                {leftOptions.map((plan) => (
                  <button
                    key={plan._id}
                    className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-text hover:bg-canvas"
                    onClick={() => { setLeftIdx(plans.indexOf(plan)); setLeftOpen(false) }}
                  >
                    <span className="text-lg tet-text font-bold">
                      {plan.name}
                    </span>

                    {plans.indexOf(plan) === leftIdx && <CheckIcon className="w-5 h-5 text-primary stroke-2 shrink-0" />}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Divider */}
        <div className="w-px bg-neutral_3 mx-2 self-stretch" />

        {/* Right selector */}
        <div className="flex-1 flex flex-col gap-2">
          <div className="relative">
            <button
              className="w-full flex items-center justify-between gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-text"
              onClick={() => { setRightOpen((v) => !v); setLeftOpen(false) }}
            >
              <span className="flex flex-col items-start min-w-0">
                <span className="text-base sm:text-lg text-white">
                  {rightPlan.name}
                </span>

                <span className={`overflow-hidden transition-all duration-300 ease-out ${stuck && rightPrice ? "max-h-5 opacity-100" : "max-h-0 opacity-0"}`}>
                  <span className="text-xs font-medium text-neutral_1">{rightPrice}</span>
                </span>
              </span>

              <ChevronDownIcon className={`w-5 h-5 shrink-0 text-white stroke-2 transition-transform duration-200 ${rightOpen ? "rotate-180" : ""}`} />
            </button>

            {rightOpen && (
              <div className="absolute z-20 mt-1 w-full rounded-xl border border-bordered bg-paper shadow-lg overflow-hidden">
                {rightOptions.map((plan) => (
                  <button
                    key={plan._id}
                    className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-text hover:bg-canvas"
                    onClick={() => { setRightIdx(plans.indexOf(plan)); setRightOpen(false) }}
                  >
                    <span className="text-lg tet-text font-bold">
                      {plan.name}
                    </span>

                    {plans.indexOf(plan) === rightIdx && <CheckIcon className="w-5 h-5 text-primary stroke-2 shrink-0" />}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Feature list */}
      <div className="rounded-b-2xl border border-bordered overflow-hidden">
        {grouped.map((group, gi) =>
          group.features.length > 0 && (
            <div key={group.category._id || gi}>
              <div className={`px-4 py-2.5 bg-neutral_1/60 text-sm font-bold text-text tracking-wide ${gi > 0 ? "border-t border-bordered/60" : ""}`}>
                {group.category.name}
              </div>

              {group.features.map((feature, fi) => (
                <div key={feature._id || fi} className={`${fi % 2 === 0 ? "bg-paper" : "bg-canvas"} `}>
                  <div className="px-4 pt-2.5 pb-1 text-sm text-text font-medium text-center">
                    {feature.name}

                    {feature.tooltip && (
                      <Tooltip 
                        text={feature.tooltip} 
                        position="top"
                      >
                        <span className="ml-1 text-text_secondary cursor-help">ⓘ</span>
                      </Tooltip>
                    )}
                  </div>

                  <div className="mx-4 border-t border-bordered/40" />

                  <div className="px-4 pb-2.5 pt-2 flex gap-2">
                    <div className="flex-1 flex justify-center">
                      {renderValue(feature, leftPlan)}
                    </div>
                    <div className="flex-1 flex justify-center">
                      {renderValue(feature, rightPlan)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  )
}